/**
 * Client-side motion reel (Ken Burns variants) via Canvas + MediaRecorder.
 */

function isLocalUrl(url: string): boolean {
  return url.startsWith('blob:') || url.startsWith('data:');
}

export async function loadImage(url: string): Promise<{
  img: HTMLImageElement;
  cleanup: () => void;
}> {
  let objectUrl: string | null = null;
  const cleanups: Array<() => void> = [];

  const fromBlob = async (blob: Blob) => {
    const ou = URL.createObjectURL(blob);
    objectUrl = ou;
    cleanups.push(() => URL.revokeObjectURL(ou));
    return loadHtmlImage(ou, false);
  };

  // Prefer decoding via blob so canvas never depends on Storage CORS.
  if (isLocalUrl(url)) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        const img = await fromBlob(await res.blob());
        return {
          img,
          cleanup: () => cleanups.forEach((fn) => fn()),
        };
      }
    } catch {
      /* fall through */
    }
    const img = await loadHtmlImage(url, false);
    return { img, cleanup: () => cleanups.forEach((fn) => fn()) };
  }

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (res.ok) {
      const img = await fromBlob(await res.blob());
      return {
        img,
        cleanup: () => cleanups.forEach((fn) => fn()),
      };
    }
  } catch {
    /* CORS or network — try image element paths below */
  }

  try {
    const img = await loadHtmlImage(url, true);
    return { img, cleanup: () => cleanups.forEach((fn) => fn()) };
  } catch {
    /* last resort without CORS flag (may taint canvas) */
  }

  try {
    const img = await loadHtmlImage(url, false);
    // Probe taint early
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    const pctx = probe.getContext('2d');
    pctx?.drawImage(img, 0, 0, 1, 1);
    pctx?.getImageData(0, 0, 1, 1);
    return { img, cleanup: () => cleanups.forEach((fn) => fn()) };
  } catch {
    throw new Error(
      'Could not load photo for reel (CORS). Re-upload the image, then Preview motion again.'
    );
  }
}

function loadHtmlImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    if (crossOrigin) el.crossOrigin = 'anonymous';
    el.onload = () => {
      if (!el.naturalWidth) {
        reject(new Error('Image decoded with zero size'));
        return;
      }
      resolve(el);
    };
    el.onerror = () => reject(new Error('Failed to load image for reel'));
    el.src = src;
  });
}

/** Best MediaRecorder container/codec this browser supports, most compact first. */
export function pickMimeType(): string | undefined {
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ];
  for (const type of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return undefined;
}

export interface KenBurnsProgress {
  frame: number;
  total: number;
  percent: number;
}

export interface KenBurnsOptions {
  imageUrl: string;
  durationSec?: number;
  width?: number;
  height?: number;
  /** Start scale relative to cover fit (1 = cover, >1 zooms in) */
  startScale?: number;
  endScale?: number;
  /** Horizontal drift amplitude as fraction of width (signed via ease) */
  panX?: number;
  /** Vertical drift (negative = upward) as fraction of height */
  panY?: number;
  onProgress?: (p: KenBurnsProgress) => void;
}

/**
 * Render a short vertical motion clip and return a downloadable Blob + object URL.
 */
export async function createKenBurnsReel(
  opts: KenBurnsOptions
): Promise<{ blob: Blob; url: string; mimeType: string }> {
  const durationSec = Math.max(3, Math.min(8, opts.durationSec ?? 5));
  // Keep encode modest for MediaRecorder reliability on Windows Chrome/Edge
  const maxSide = 540;
  let width = opts.width ?? 1080;
  let height = opts.height ?? 1920;
  const scaleDown = Math.min(1, maxSide / Math.max(width, height));
  width = Math.max(2, Math.round((width * scaleDown) / 2) * 2);
  height = Math.max(2, Math.round((height * scaleDown) / 2) * 2);

  const startScale = opts.startScale ?? 1.05;
  const endScale = opts.endScale ?? 1.22;
  const panX = opts.panX ?? 0.04;
  const panY = opts.panY ?? -0.06;

  const { img, cleanup } = await loadImage(opts.imageUrl);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas unsupported');

    if (typeof MediaRecorder === 'undefined' || typeof canvas.captureStream !== 'function') {
      throw new Error('Browser cannot record video — try Chrome or Edge');
    }

    // Warm first paint before captureStream (helps some browsers)
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, width, height);
    const cover0 = Math.max(width / img.naturalWidth, height / img.naturalHeight);
    const dw0 = img.naturalWidth * cover0 * startScale;
    const dh0 = img.naturalHeight * cover0 * startScale;
    ctx.drawImage(img, (width - dw0) / 2, (height - dh0) / 2, dw0, dh0);

    const stream = canvas.captureStream(30);
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 })
        : new MediaRecorder(stream, { videoBitsPerSecond: 2_500_000 });
    } catch {
      try {
        recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      } catch {
        throw new Error('Could not start video recorder in this browser');
      }
    }

    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };

    const done = new Promise<Blob>((resolve, reject) => {
      recorder.onerror = () => {
        stream.getTracks().forEach((t) => t.stop());
        reject(new Error('Reel recording failed'));
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = recorder.mimeType || mimeType || 'video/webm';
        const blob = new Blob(chunks, { type });
        if (blob.size < 500) {
          reject(
            new Error(
              'Reel recording was empty. Try Chrome/Edge, or re-upload the photo and Preview again.'
            )
          );
          return;
        }
        resolve(blob);
      };
    });

    if (recorder.state === 'inactive') {
      recorder.start(200);
    }

    // Ensure at least one paint is captured
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    const cover = Math.max(width / img.naturalWidth, height / img.naturalHeight);
    const fps = 30;
    const totalFrames = Math.round(durationSec * fps);

    for (let i = 0; i < totalFrames; i++) {
      if (recorder.state === 'inactive') {
        throw new Error('Reel recorder stopped unexpectedly');
      }
      const t = i / Math.max(1, totalFrames - 1);
      const ease = t * t * (3 - 2 * t);
      const scale = cover * (startScale + (endScale - startScale) * ease);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      const x = (width - dw) / 2 + (ease - 0.5) * width * panX * 2;
      const y = (height - dh) / 2 + ease * height * panY;

      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, width, height);
      try {
        ctx.drawImage(img, x, y, dw, dh);
      } catch {
        throw new Error(
          'Could not draw photo into reel (CORS). Re-upload the image, then Preview motion again.'
        );
      }

      if (opts.onProgress && (i % 3 === 0 || i === totalFrames - 1)) {
        opts.onProgress({
          frame: i + 1,
          total: totalFrames,
          percent: Math.round(((i + 1) / totalFrames) * 100),
        });
      }

      await new Promise<void>((r) => requestAnimationFrame(() => r()));
    }

    if (typeof recorder.requestData === 'function' && recorder.state === 'recording') {
      try {
        recorder.requestData();
      } catch {
        /* ignore */
      }
    }
    if (recorder.state === 'recording' || recorder.state === 'paused') {
      recorder.stop();
    } else {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error('Reel recorder was not active');
    }

    const blob = await done;
    const outUrl = URL.createObjectURL(blob);
    return { blob, url: outUrl, mimeType: blob.type || mimeType || 'video/webm' };
  } finally {
    cleanup();
  }
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
